// src/components/black-friday/dashboard-view.tsx
import React, { useState, useMemo } from "react";
import { BFStorageData, BFDailyCampaignData } from "@/types/black-friday";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { getCampaignOptions, aggregateChannelData } from "@/lib/black-friday-data-utils";
import MetricCards from "./metric-cards";
import ComparisonCharts from "./comparison-charts";
import ProductComparisonView from "./product-comparison-view";
import SearchTermComparisonView from "./search-term-comparison-view";
import CampaignTypesView from "./campaign-types-view";
import InsightsView from "./insights-view";
import AgentPromoBanner from "./agent-promo-banner";
import ChartModal from "./chart-modal";

interface Props {
  data: BFStorageData;
}

export type ComparisonPeriod = 'year2024_total' | 'year2024_same' | 'year2023_total' | 'year2023_same';

export default function BlackFridayDashboardView({ data }: Props) {
  const [selectedCampaign, setSelectedCampaign] = useState<string>("All Campaigns");
  const [showAllCampaigns, setShowAllCampaigns] = useState<boolean>(true);
  const [comparisonPeriod, setComparisonPeriod] = useState<ComparisonPeriod>('year2024_same');
  const [expandedChart, setExpandedChart] = useState<{ title: string; data: any[]; format: "number" | "currency" | "percentage" } | null>(null);

  // Get campaign options (use newest available data)
  const campaignOptions = useMemo(() => {
    const source = data.dailyCampaign25 || data.dailyCampaign24 || data.dailyCampaign23;
    if (!source) return [{ value: "All Campaigns", label: "All Campaigns" }];
    return getCampaignOptions(source);
  }, [data.dailyCampaign25, data.dailyCampaign24, data.dailyCampaign23]);

  // Get current campaign index
  const currentCampaignIndex = useMemo(() => {
    return campaignOptions.findIndex(opt => opt.value === selectedCampaign);
  }, [campaignOptions, selectedCampaign]);

  // Check if we have any products data
  const hasProductsData = useMemo(() => {
    return (
      (data.products25 && data.products25.length > 0) ||
      (data.products24 && data.products24.length > 0) ||
      (data.products23 && data.products23.length > 0)
    );
  }, [data.products25, data.products24, data.products23]);

  // Navigation functions
  const navigateToPreviousCampaign = () => {
    if (currentCampaignIndex > 0) {
      const previousCampaign = campaignOptions[currentCampaignIndex - 1].value;
      setSelectedCampaign(previousCampaign);
      // If going back to "All Campaigns", turn on the switch
      if (previousCampaign === "All Campaigns") {
        setShowAllCampaigns(true);
      } else {
        setShowAllCampaigns(false);
      }
    }
  };

  const navigateToNextCampaign = () => {
    if (currentCampaignIndex < campaignOptions.length - 1) {
      const nextCampaign = campaignOptions[currentCampaignIndex + 1].value;
      setSelectedCampaign(nextCampaign);
      // Automatically turn off "Show All Campaigns" when navigating to individual campaigns
      setShowAllCampaigns(false);
    }
  };

  // Sync showAllCampaigns with selectedCampaign - ensure switch reflects current state
  useMemo(() => {
    if (showAllCampaigns && selectedCampaign !== "All Campaigns") {
      setSelectedCampaign("All Campaigns");
    } else if (!showAllCampaigns && selectedCampaign === "All Campaigns") {
      setShowAllCampaigns(true);
    }
  }, [showAllCampaigns, selectedCampaign]);

  // Filter data by selected campaign (2025 data)
  const filteredData25 = useMemo(() => {
    const source = data.dailyCampaign25;
    const allCampaignsSource = data.dailyAllCampaigns25;

    if (!source) return [];
    if (selectedCampaign === "All Campaigns") {
      return allCampaignsSource || [];
    }
    return source.filter((row) => row.campaign === selectedCampaign);
  }, [data.dailyCampaign25, data.dailyAllCampaigns25, selectedCampaign]);

  // Filter data by selected campaign (2024 data)
  const filteredData24 = useMemo(() => {
    const source = data.dailyCampaign24;
    const allCampaignsSource = data.dailyAllCampaigns24;

    if (!source) return [];
    if (selectedCampaign === "All Campaigns") {
      return allCampaignsSource || [];
    }
    return source.filter((row) => row.campaign === selectedCampaign);
  }, [data.dailyCampaign24, data.dailyAllCampaigns24, selectedCampaign]);

  // Filter data by selected campaign (2023 data)
  const filteredData23 = useMemo(() => {
    const source = data.dailyCampaign23;
    const allCampaignsSource = data.dailyAllCampaigns23;

    if (!source) return [];
    if (selectedCampaign === "All Campaigns") {
      return allCampaignsSource || [];
    }
    return source.filter((row) => row.campaign === selectedCampaign);
  }, [data.dailyCampaign23, data.dailyAllCampaigns23, selectedCampaign]);

  // Get max index from 2025 data for "same period" comparison
  const maxIndex25 = useMemo(() => {
    const allData25 = data.dailyAllCampaigns25 || [];
    if (allData25.length === 0) return 0;
    return Math.max(...allData25.map(row => row.index));
  }, [data.dailyAllCampaigns25]);

  // Calculate comparison period labels with dates and day counts
  const comparisonLabels = useMemo(() => {
    // Black Friday dates for each year
    const bf2025 = new Date('2025-11-28');
    const bf2024 = new Date('2024-11-29');
    const bf2023 = new Date('2023-11-24');

    // Calculate number of days in current 2025 period (from -70 to maxIndex25)
    const daysSoFar2025 = maxIndex25 + 71; // +71 because we start at -70

    // Full period is 10 weeks before + 2 weeks after = 84 days
    const totalDays = 84;

    // Helper to format date range
    const formatDateRange = (startDate: Date, endDate: Date) => {
      const formatDate = (d: Date) => {
        const month = d.toLocaleDateString('en-US', { month: 'short' });
        const day = d.getDate();
        return `${month} ${day}`;
      };
      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    };

    // Calculate date ranges for each period
    const get2024SamePeriod = () => {
      const startDate = new Date(bf2024);
      startDate.setDate(startDate.getDate() - 70);
      const endDate = new Date(bf2024);
      endDate.setDate(endDate.getDate() + maxIndex25);
      return formatDateRange(startDate, endDate);
    };

    const get2024TotalPeriod = () => {
      const startDate = new Date(bf2024);
      startDate.setDate(startDate.getDate() - 70);
      const endDate = new Date(bf2024);
      endDate.setDate(endDate.getDate() + 14);
      return formatDateRange(startDate, endDate);
    };

    const get2023SamePeriod = () => {
      const startDate = new Date(bf2023);
      startDate.setDate(startDate.getDate() - 70);
      const endDate = new Date(bf2023);
      endDate.setDate(endDate.getDate() + maxIndex25);
      return formatDateRange(startDate, endDate);
    };

    const get2023TotalPeriod = () => {
      const startDate = new Date(bf2023);
      startDate.setDate(startDate.getDate() - 70);
      const endDate = new Date(bf2023);
      endDate.setDate(endDate.getDate() + 14);
      return formatDateRange(startDate, endDate);
    };

    return {
      year2024_same: `2024 Same Period (${daysSoFar2025} days: ${get2024SamePeriod()})`,
      year2024_total: `2024 Full Period (${totalDays} days: ${get2024TotalPeriod()})`,
      year2023_same: `2023 Same Period (${daysSoFar2025} days: ${get2023SamePeriod()})`,
      year2023_total: `2023 Full Period (${totalDays} days: ${get2023TotalPeriod()})`,
    };
  }, [maxIndex25]);

  // Comparison labels for metric cards
  const comparisonLabelsShort = useMemo(() => {
    return {
      year2024_same: '2024 (Same period as 2025 so far)',
      year2024_total: '2024 (Full BF period: 10 weeks before to 2 weeks after)',
      year2023_same: '2023 (Same period as 2025 so far)',
      year2023_total: '2023 (Full BF period: 10 weeks before to 2 weeks after)',
    };
  }, []);

  // Filter comparison data based on selected period
  const getComparisonData = (yearData: BFDailyCampaignData[], period: ComparisonPeriod) => {
    if (period === 'year2024_same') {
      return yearData.filter(row => row.index <= maxIndex25);
    } else if (period === 'year2023_same') {
      return yearData.filter(row => row.index <= maxIndex25);
    }
    // For 'total' periods, return all data
    return yearData;
  };

  // Generate channel data for each year from campaign data
  // Always aggregate from campaign data to ensure year-specific data
  const channelData25 = useMemo(() => {
    if (data.dailyCampaign25 && data.dailyCampaign25.length > 0) {
      return aggregateChannelData(data.dailyCampaign25);
    }
    return [];
  }, [data.dailyCampaign25]);

  const channelData24 = useMemo(() => {
    if (data.dailyCampaign24 && data.dailyCampaign24.length > 0) {
      return aggregateChannelData(data.dailyCampaign24);
    }
    return [];
  }, [data.dailyCampaign24]);

  const channelData23 = useMemo(() => {
    if (data.dailyCampaign23 && data.dailyCampaign23.length > 0) {
      return aggregateChannelData(data.dailyCampaign23);
    }
    return [];
  }, [data.dailyCampaign23]);

  // Calculate aggregated metrics for selected campaign with comparison period
  const aggregatedMetrics = useMemo(() => {
    const totals25 = filteredData25.reduce(
      (acc, row) => ({
        impr: acc.impr + row.impr,
        clicks: acc.clicks + row.clicks,
        cost: acc.cost + row.cost,
        conv: acc.conv + row.conv,
        convValue: acc.convValue + row.convValue,
      }),
      { impr: 0, clicks: 0, cost: 0, conv: 0, convValue: 0 },
    );

    // Get comparison data based on selected period
    let comparisonData: BFDailyCampaignData[];
    if (comparisonPeriod === 'year2024_total' || comparisonPeriod === 'year2024_same') {
      comparisonData = getComparisonData(filteredData24, comparisonPeriod);
    } else {
      comparisonData = getComparisonData(filteredData23, comparisonPeriod);
    }

    const totalsComparison = comparisonData.reduce(
      (acc, row) => ({
        impr: acc.impr + row.impr,
        clicks: acc.clicks + row.clicks,
        cost: acc.cost + row.cost,
        conv: acc.conv + row.conv,
        convValue: acc.convValue + row.convValue,
      }),
      { impr: 0, clicks: 0, cost: 0, conv: 0, convValue: 0 },
    );

    return { year2025: totals25, comparison: totalsComparison };
  }, [filteredData25, filteredData24, filteredData23, comparisonPeriod, maxIndex25]);

  return (
    <div className="space-y-6">
      {/* Main Tabs */}
      <Tabs defaultValue="overview" className="w-full">
        {/* Tabs with comparison selector on the right */}
        <div className="flex items-center justify-between gap-4 bg-muted p-1 rounded-lg">
          {/* Left: Tabs */}
          <TabsList className="flex h-auto p-0 bg-transparent gap-2">
          <TabsTrigger
            value="overview"
            className="data-[state=active]:bg-blue-500 data-[state=active]:text-white data-[state=inactive]:text-foreground font-semibold py-3 px-4 relative border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=inactive]:border-blue-400"
          >
            Campaign Overview
          </TabsTrigger>
          <TabsTrigger
            value="campaign-types"
            className="data-[state=active]:bg-purple-500 data-[state=active]:text-white data-[state=inactive]:text-foreground font-semibold py-3 px-4 relative border-b-2 border-transparent data-[state=active]:border-purple-500 data-[state=inactive]:border-purple-400"
          >
            Campaign Types
          </TabsTrigger>
          {hasProductsData && (
            <TabsTrigger
              value="products"
              className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white data-[state=inactive]:text-foreground font-semibold py-3 px-4 relative border-b-2 border-transparent data-[state=active]:border-emerald-500 data-[state=inactive]:border-emerald-400"
            >
              Top Products
            </TabsTrigger>
          )}
          <TabsTrigger
            value="insights"
            className="data-[state=active]:bg-orange-500 data-[state=active]:text-white data-[state=inactive]:text-foreground font-semibold py-3 px-4 relative border-b-2 border-transparent data-[state=active]:border-orange-500 data-[state=inactive]:border-orange-400"
          >
            Insights
          </TabsTrigger>
          <TabsTrigger
            value="search-terms"
            className="data-[state=active]:bg-amber-500 data-[state=active]:text-white data-[state=inactive]:text-foreground font-semibold py-3 px-4 relative border-b-2 border-transparent data-[state=active]:border-amber-500 data-[state=inactive]:border-amber-400"
          >
            80/20 Search Terms
          </TabsTrigger>
        </TabsList>

        {/* Right: Comparison selector */}
        <div className="flex items-center gap-2 whitespace-nowrap">
          <label htmlFor="comparison-select" className="text-sm font-medium">
            Compare 2025 to:
          </label>
          <Select value={comparisonPeriod} onValueChange={(value: ComparisonPeriod) => setComparisonPeriod(value)}>
            <SelectTrigger id="comparison-select" className="w-[364px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="year2024_same">{comparisonLabels.year2024_same}</SelectItem>
              <SelectItem value="year2024_total">{comparisonLabels.year2024_total}</SelectItem>
              <SelectItem value="year2023_same">{comparisonLabels.year2023_same}</SelectItem>
              <SelectItem value="year2023_total">{comparisonLabels.year2023_total}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6 mt-6">
          {/* Campaign Controls - single row layout */}
          <div className="flex items-center gap-3 flex-wrap">
            {/* Campaign Label */}
            <label htmlFor="campaign-select" className="text-sm font-medium whitespace-nowrap">
              Campaign:
            </label>

            {/* Campaign Dropdown - 40% smaller width */}
            <Select
              value={selectedCampaign}
              onValueChange={(value) => {
                setSelectedCampaign(value);
                // Update switch state based on selection
                setShowAllCampaigns(value === "All Campaigns");
              }}
            >
              <SelectTrigger id="campaign-select" className="w-[360px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-[480px] overflow-y-auto">
                {campaignOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Previous Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={navigateToPreviousCampaign}
              disabled={currentCampaignIndex <= 0}
              className="h-10 px-3"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>

            {/* Next Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={navigateToNextCampaign}
              disabled={
                currentCampaignIndex >= campaignOptions.length - 1 ||
                currentCampaignIndex === -1
              }
              className="h-10 px-3"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>

            {/* Show All Campaigns Toggle */}
            <div className="flex items-center space-x-2">
              <Switch
                id="all-campaigns"
                checked={showAllCampaigns}
                onCheckedChange={setShowAllCampaigns}
              />
              <Label htmlFor="all-campaigns" className="text-sm font-medium whitespace-nowrap">
                Show All Campaigns Combined
              </Label>
            </div>
          </div>

          {/* Metric Cards */}
          <MetricCards
            metrics={{
              year2025: aggregatedMetrics.year2025,
              year2024: aggregatedMetrics.comparison
            }}
            comparisonLabel={comparisonLabelsShort[comparisonPeriod]}
          />

          {/* Comparison Charts */}
          <ComparisonCharts
            data25={filteredData25}
            data24={filteredData24}
            data23={filteredData23}
            comparisonPeriod={comparisonPeriod}
            maxIndex25={maxIndex25}
            onChartClick={(title, data, format) => setExpandedChart({ title, data, format })}
          />
        </TabsContent>

        {/* Campaign Types Tab */}
        <TabsContent value="campaign-types" className="space-y-6 mt-6">
          <CampaignTypesView
            channelData25={channelData25}
            channelData24={channelData24}
            channelData23={channelData23}
            comparisonPeriod={comparisonPeriod}
          />
        </TabsContent>

        {/* Products Tab */}
        <TabsContent value="products" className="space-y-6 mt-6">
          <ProductComparisonView
            products25={data.products25 || []}
            products24={data.products24 || []}
            products23={data.products23 || []}
            comparisonPeriod={comparisonPeriod}
          />
        </TabsContent>

        {/* Insights Tab */}
        <TabsContent value="insights" className="space-y-6 mt-6">
          <InsightsView dashboard={data.dashboard} />
        </TabsContent>

        {/* Search Terms Tab */}
        <TabsContent value="search-terms" className="space-y-6 mt-6">
          <SearchTermComparisonView
            searchTerms25={data.searchTerms25 || []}
            searchTerms24={data.searchTerms24 || []}
            searchTerms23={data.searchTerms23 || []}
            pMaxCategories25={data.pMaxCategories25 || []}
            pMaxCategories24={data.pMaxCategories24 || []}
            pMaxCategories23={data.pMaxCategories23 || []}
            comparisonPeriod={comparisonPeriod}
          />
        </TabsContent>
      </Tabs>

      {/* 8020agent Promo Banner */}
      <AgentPromoBanner />

      {/* Privacy Note */}
      <div className="text-center py-4">
        <p className="text-xs text-muted-foreground max-w-2xl mx-auto">
          <strong>Your Data Privacy:</strong> All your Black Friday data remains securely stored in your own Google Sheet.
          We do not store your data in any database, and we cannot see or access your campaign information.
          Your data is completely under your control.
        </p>

        {/* Footer Credit */}
        <p className="text-xs text-muted-foreground text-center mt-6">
          Created by{" "}
          <a
            href="https://mikerhodes.com.au/"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline text-foreground font-medium"
          >
            Mike Rhodes
          </a>{" "}
          © {new Date().getFullYear()}
        </p>
      </div>

      {/* Chart Expansion Modal */}
      {expandedChart && (
        <ChartModal
          isOpen={!!expandedChart}
          onClose={() => setExpandedChart(null)}
          title={expandedChart.title}
          data={expandedChart.data}
          format={expandedChart.format}
        />
      )}
    </div>
  );
}
